import { useTranslation } from 'react-i18next'

import type { InputUnitType } from 'aem-libs-ts/src/constants/unitType'
import type { Maybe } from 'aem-libs-ts/src/types/utility'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'
import { numberFormat } from 'aem-libs-ts/src/utils/formatting/numbers/numberFormat'

import cs from './i18n/cs.json'
import en from './i18n/en.json'


const DEFAULT_LANG_VALUE = 2

export const useUnitTranslateFunction = () => {
  const { t, i18n } = useTranslation('useUnitTranslate')
  i18n.addResourceBundle('cs', 'useUnitTranslate', cs)
  i18n.addResourceBundle('en', 'useUnitTranslate', en)

  const getUnitTranslate = (value: Maybe<number>, unitType: Maybe<InputUnitType>, unitCustom?: string) => {
    if (!unitType || !isDefined(value)) {
      return ''
    }
    return unitType === 'custom'
      ? unitCustom
      : t('UNIT_TYPE.' + unitType, { smart_count: value === null ? DEFAULT_LANG_VALUE : value })
  }
  return {
    getUnitTranslate
  }
}

export const useUnitTranslate = (value: Maybe<number>, unitType: Maybe<InputUnitType>, unitCustom?: string) => {
  const { getUnitTranslate } = useUnitTranslateFunction()
  return getUnitTranslate(value, unitType, unitCustom)
}

export const useValueUnitLabelFcn = (options?: { separateThousands?: boolean }) => {
  const { getUnitTranslate } = useUnitTranslateFunction()

  const parseValue = (value: Maybe<number>, unitType: InputUnitType, unitCustom?: string) => {
    if (!isDefined(value)) {
      return { label: '', parsedValue: null }
    }

    return pipe(() => value)
      .map((v) => {
        const unit = getUnitTranslate(v, unitType, unitCustom)
        return {
          unit,
          value: options?.separateThousands ? numberFormat(v) : v.toString()
        }
      })
      .map((parsedValue) => ({ parsedValue, label: `${parsedValue.value} ${parsedValue.unit}` }))
      .get()
  }

  const getUnitLabel = (value: Maybe<number>, unitType: InputUnitType, unitCustom?: string) =>
    parseValue(value, unitType, unitCustom).label

  const getParsedUnitValue = (value: Maybe<number>, unitType: InputUnitType, unitCustom?: string) =>
    parseValue(value, unitType, unitCustom)

  return { getUnitLabel, getParsedUnitValue }
}
